import { ICountry } from '@common/types/country';
import { InboxModal } from '@inbox/components/display/InboxModal';
import {
    useDeleteInboxMutation,
    useUpdateInboxMutation,
    useActivateInboxMutation,
    useDeactivateInboxMutation,
    useBulkActivateInboxMutation,
    useBulkDeactivateInboxMutation,
    useAddInboxMutation,
} from '@inbox/services';
import { IInbox, InboxForm, InboxModalType } from '@inbox/types';
import { useGetManagedCountriesQuery } from '@services/accounts';
import { createContext, PropsWithChildren, useContext, useState } from 'react';

interface IInboxContext {
    onAddInbox: (inboxData: InboxForm) => Promise<IInbox>;
    onUpdateInbox: (inboxData: InboxForm) => Promise<IInbox>;
    onDelete: (item: Partial<IInbox>) => Promise<void>;
    onBulkDelete: (inboxIds: number[]) => Promise<void>;
    onToggleActive: (item: Partial<IInbox>) => Promise<IInbox>;
    onToggleInactive: (item: Partial<IInbox>) => Promise<IInbox>;
    onBulkToggleActiveStatus: (inboxIds: number[]) => Promise<IInbox[]>;
    onBulkToggleInactiveStatus: (inboxIds: number[]) => Promise<IInbox[]>;
    onOpenAddModal: () => void;
    onOpenEditModal: (data: Partial<IInbox>) => void;
    onCloseModal: () => void;
    toggleSelectedItem: (inboxId: number) => void;
    updateSelectedItems: (inboxIds: number[]) => void;
    removeSelectedItems: (inboxIds: number[]) => void;
    selectedItems: number[];
    isDeleting?: boolean;
    isUpdating?: boolean;
    isAdding?: boolean;
    isActivating?: boolean;
    isDeactivating?: boolean;
    isBulkActivating?: boolean;
    isBulkDeactivating?: boolean;
    managedCountries?: ICountry[];
}

const InboxContext = createContext<IInboxContext | undefined>(undefined);

export const InboxContextProvider = ({ children }: PropsWithChildren) => {
    const [initialData, setInitialData] = useState<Partial<IInbox> | undefined>(undefined);
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<InboxModalType>('add');
    const [selectedItems, setSelectedItems] = useState<number[]>([]);

    const [updateInbox, { isLoading: isUpdating }] = useUpdateInboxMutation();
    const [addInbox, { isLoading: isAdding }] = useAddInboxMutation();

    const [deleteInbox, { isLoading: isDeleting }] = useDeleteInboxMutation();
    const [activateInbox, { isLoading: isActivating }] = useActivateInboxMutation();
    const [deactivateInbox, { isLoading: isDeactivating }] = useDeactivateInboxMutation();
    const [bulkActivateInbox, { isLoading: isBulkActivating }] = useBulkActivateInboxMutation();
    const [bulkDeactivateInbox, { isLoading: isBulkDeactivating }] = useBulkDeactivateInboxMutation();

    const { data: managedCountries } = useGetManagedCountriesQuery();

    const toggleSelectedItem = (inboxId: number) => {
        setSelectedItems((prev) => (prev.includes(inboxId) ? prev.filter((id) => id !== inboxId) : [...prev, inboxId]));
    };

    const updateSelectedItems = (inboxIds: number[]) => {
        setSelectedItems(inboxIds);
    };

    const removeSelectedItems = (inboxIds: number[]) => {
        setSelectedItems((prev) => prev.filter((id) => !inboxIds.includes(id)));
    };

    const handleAddInbox = (inboxData: InboxForm) => {
        return addInbox(inboxData).unwrap();
    };

    const handleUpdateInbox = (inboxData: InboxForm) => {
        if (!initialData || !initialData.id) {
            return Promise.reject(new Error('Inbox ID is required to update an inbox.'));
        }
        return updateInbox({ id: initialData.id, inboxData }).unwrap();
    };

    const handleDelete = (item: Partial<IInbox>) => {
        if (item.id) {
            return deleteInbox([item.id]).unwrap();
        }
        return Promise.reject(new Error('Inbox ID is required to delete an inbox.'));
    };

    const handleBulkDelete = (inboxIds: number[]) => {
        return deleteInbox(inboxIds).unwrap();
    };

    const handleToggleActiveStatus = (item: Partial<IInbox>) => {
        if (item.id) {
            return activateInbox({ id: item.id }).unwrap();
        }
        return Promise.reject(new Error('Inbox ID is required to activate an inbox.'));
    };

    const handleToggleInactiveStatus = (item: Partial<IInbox>) => {
        if (item.id) {
            return deactivateInbox({ id: item.id }).unwrap();
        }
        return Promise.reject(new Error('Inbox ID is required to deactivate an inbox.'));
    };

    const handleBulkToggleActiveStatus = (inboxIds: number[]) => {
        return bulkActivateInbox({ ids: inboxIds }).unwrap();
    };

    const handleBulkToggleInactiveStatus = (inboxIds: number[]) => {
        return bulkDeactivateInbox({ ids: inboxIds }).unwrap();
    };

    const handleOpenAddModal = () => {
        setMode('add');
        setModalOpen(true);
        setInitialData(undefined);
    };

    const handleOpenEditModal = (data: Partial<IInbox>) => {
        setInitialData(data);
        setMode('edit');
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    return (
        <InboxContext.Provider
            value={{
                onUpdateInbox: handleUpdateInbox,
                onDelete: handleDelete,
                onBulkDelete: handleBulkDelete,
                onToggleActive: handleToggleActiveStatus,
                onToggleInactive: handleToggleInactiveStatus,
                onBulkToggleActiveStatus: handleBulkToggleActiveStatus,
                onBulkToggleInactiveStatus: handleBulkToggleInactiveStatus,
                onOpenAddModal: handleOpenAddModal,
                onAddInbox: handleAddInbox,
                onOpenEditModal: handleOpenEditModal,
                onCloseModal: handleCloseModal,
                toggleSelectedItem,
                updateSelectedItems,
                removeSelectedItems,
                isDeleting,
                isUpdating,
                isActivating,
                isDeactivating,
                isBulkActivating,
                isBulkDeactivating,
                isAdding,
                managedCountries,
                selectedItems,
            }}
        >
            {children}
            <InboxModal open={modalOpen} mode={mode} selectedInbox={initialData} onClose={handleCloseModal} />
        </InboxContext.Provider>
    );
};

export const useInboxContext = () => {
    const context = useContext(InboxContext);
    if (!context) {
        throw new Error('useInboxContext must be used within an InboxContextProvider');
    }
    return context;
};
